import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
// Cloud React components
import { Grid } from '@mui/material';
import { Button, Input, Form, Checkbox } from 'Components';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';
import IconPicker from 'Modules/.common/components/IconPicker';
import Tabs, { TabContainer } from 'Components/Tabs';
import TableView from 'Components/TableView';
import InputSearch from 'Components/InputSearch';
import TagsManage from 'Modules/.common/components/TagsManage';

//actions
import {
    clearRedux,
    accountTagsOpenCloseMergeTagsModal,
    accountTagsMergeTagsSave,
    accountTagsMergeTagsSearchEntities
} from 'Redux/actions';
import { validateTagPermissions } from '../..';

const MergeTags = (props) => {
    const dispatch = useDispatch();
    const { callback } = props;
    const { messages } = useIntl();

    const tableViewRef = useRef();
    const mergeTags = useRef();

    const open = useSelector(state => state.mergeTagsRedux.open);
    const selected = useSelector(state => state.mergeTagsRedux.selected);
    const response = useSelector(state => state.mergeTagsRedux.response);
    const loading = useSelector(state => state.mergeTagsRedux.loading);
    const entities = useSelector(state => state.mergeTagsRedux.entities);
    const loadingEntities = useSelector(state => state.mergeTagsRedux.loadingEntities);
    const filters = useSelector(state => state.mergeTagsRedux.filters);
    const total = useSelector(state => state.mergeTagsRedux.total);
    const user = useSelector((state) => state.securityRedux.user);

    const [formData, setFormData] = useState({});
    const [errorMessage, setErrorMessage] = useState({});
    const [tab, setTab] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [openConfirmation, setOpenConfirmation] = useState(false);

    useEffect(() => {
        return () => {
            dispatch(clearRedux("ACCOUNT_TAGS_MERGE_TAGS"));
            setFormData({});
        }
    }, []);

    useEffect(() => {
        if (open && selected) {
            setFormData({ ...formData, tags: selected });
        }
    }, [selected, open])

    useEffect(() => {
        if (response && open) {
            if (response.error) {
                if (response.errorMessage === "CompanyReadonly") {
                    NotificationManager.error(
                        messages["errorCompanyReadOnly"],
                        messages["error"],
                        3000,
                        null,
                        null,
                        "error"
                    );
                }

                dispatch(clearRedux("ACCOUNT_TAGS_MERGE_TAGS", { loading: false, response: null }));
            } else {
                NotificationManager.success(
                    messages['accountTagsMergeTags_success'],
                    messages['success'],
                    3000,
                    null,
                    null,
                    'success',
                );
                if (callback) callback();
                handleClose();
            }
        }
    }, [response, open]);

    const tabData = [
        {
            iconIndicator: "directions_car",
            label: messages['accountTagsMergeTags_vehicles'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            entityName: "Unit",
            permissionSubUser: [13]
        },
        {
            iconIndicator: "settings",
            label: messages['accountTagsMergeTags_assets'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            entityName: "Asset",
        },
        {
            iconIndicator: "person",
            label: messages['accountTagsMergeTags_drivers'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            permission: "driver",
            permissionSubUser: [17],
            entityName: "Driver"
        },
        {
            iconIndicator: "flag",
            label: messages['accountTagsMergeTags_landmarks'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            entityName: "Landmark",
            permissionSubUser: [3]
        },
        {
            iconIndicator: "pentagon",
            label: messages['accountTagsMergeTags_geofences'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            entityName: "Geofence",
            permissionSubUser: [1]
        },
        {
            iconIndicator: "supervisor_account",
            label: messages['accountTagsMergeTags_users'],
            style: { paddingLeft: 0, paddingRight: 0 },
            disabled: loadingEntities || !formData?.tags || !formData?.tags?.length,
            permissionSubUser: [31],
            entityName: "User"
        }
    ];
    const tabsWithPermissions = validateTagPermissions(user, tabData, true)
    const renderTable = useCallback(() => {
        return (
            <Grid
                container
                direction="column"
                justifyContent="center"
                spacing={0}
            >
                <Grid item xs={12}>
                    <InputSearch
                        placeholder={messages['search']}
                        searchText={searchText}
                        onSearch={(text) => {
                            if (window.typingTime) {
                                clearTimeout(window.typingTime)
                            }

                            setSearchText(text);
                            window.typingTime = setTimeout(() => {
                                tableViewRef?.current?.setNewPage(1);
                                dispatch(accountTagsMergeTagsSearchEntities({ ...filters, offset: 0, openSearch: text, reset: true }));
                                window.typingTime = null;
                            }, 500);
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TableView
                        ref={tableViewRef}
                        data={entities || []}
                        loading={loadingEntities}
                        rowsPerPage={filters?.limit || 0}
                        name="entities-tag-list"
                        topSpaceSize={370}
                        maxHeight={400}
                        //height={400}
                        infiniteScroll={true}
                        viewType="list"
                        messages={messages}
                        itemSelector={false}
                        serverSide
                        onChangePage={(page) => {
                            
                            if (formData?.tags && formData?.tags?.length) {
                                const entity = tabsWithPermissions.find(item => item.index === tab);
                                dispatch(accountTagsMergeTagsSearchEntities({
                                    ...filters,
                                    offset: (page - 1) * filters.limit,
                                    parentTagIds: formData?.tags?.map(tag => {
                                        return tag?.id
                                    }),
                                    entityName: entity?.entityName || "",
                                    reset: page === 1 ? true : false
                                }));
                            }
                        }}
                        total={total}
                        moreRowsAvailable={total > entities?.length}
                        renderRow={(row) => {
                            return (
                                <div style={{ padding: '5px 0px 5px 0px', fontWeight: '700' }}>
                                    {row.label}
                                </div>
                            )
                        }}
                    />
                </Grid>
            </Grid>
        )
    });

    const handleClose = useCallback(() => {
        dispatch(accountTagsOpenCloseMergeTagsModal(false));
        dispatch(clearRedux("ACCOUNT_TAGS_MERGE_TAGS"));
        setFormData({});
    });

    const onSubmit = useCallback(() => {
        if (formData.nestUnder && !formData.parentId) {
            setErrorMessage({ ...errorMessage, nestUnder: messages['required_field'] });
            return;
        }

        if (!formData?.tags || !formData?.tags?.length) {
            setErrorMessage({ ...errorMessage, tags: messages['required_field'] });
            return;
        }

        if (formData?.tags?.length < 2) {
            setErrorMessage({ ...errorMessage, tags: messages['accountTagsMergeTags_minTagsMessage'] });
            return;
        }

        setOpenConfirmation(true);
    });

    let confirmText = messages['accountTagsMergeTags_confirmation'].replace("{number_tags}", formData?.tags?.length || 0);
    confirmText = confirmText.replace("{tag_name}".formData?.label || "");
 
    return (
        <React.Fragment>
            <Modal
                title={messages['accountTagsMergeTags_title']}
                open={open}
                loading={loading}
                position="right"
                size="small"
                style={{ overflow: 'hidden' }}
                className="overflow-hidden"
                handleClose={handleClose}
            >
                <ModalBody
                    style={{
                        overflow: 'hidden',
                        marginBottom: 0
                    }}
                >
                    <div
                        style={{
                            padding: "0px 24px 24px 24px",
                            height: "calc(100vh - 225px)",
                            overflowX: 'auto'
                        }}
                    >
                        <Tabs
                            tab={0}
                            type="icon-indicator"
                            border
                            onChange={(newTab) => {
                                tableViewRef?.current?.setNewPage(1);
                                setSearchText("");
                                setTab(newTab);
                                dispatch(clearRedux("ACCOUNT_TAGS_MERGE_TAGS", {
                                    filters: { ...filters, offset: 0, openSearch: "" }
                                }));
                            }}
                        >
                            <TabContainer
                                iconIndicator="local_offer"
                                label={messages['accountTagsMergeTags_tag']}
                                index={0}
                                style={{ paddingLeft: 0, paddingRight: 0 }}
                                disabled={loading}
                            >
                                <Form ref={mergeTags} onValidSubmit={onSubmit}>
                                    <div style={{ width: '100%', marginTop: 10, marginBottom: 10 }}>
                                        <TagsManage
                                            id="tags"
                                            label={messages["accountTagsMergeTags_tags"]}
                                            placeholder={messages["accountTagsMergeTags_tags"]}
                                            value={formData?.tags}
                                            mode="selector"
                                            onlyTags={true}
                                            height={100}
                                            menuStyle={{width: 430, height: 300}}
                                            isVisiblePill={true}
                                            multiple={true}
                                            disableInheritance={true}
                                            disableUntaggedSelection={true}
                                            onChange={(values) => {
                                                setErrorMessage({});
                                                setFormData({ ...formData, tags: values });
                                            }}
                                        />

                                        {
                                            errorMessage?.tags && <small className='text-danger'>{errorMessage?.tags}</small>
                                        }
                                    </div>

                                    <div style={{ width: '100%' }}>
                                        <label className='input-normal-label'>
                                            {messages['accountTagsMergeTags_color']} <span className="text-danger"> *</span>
                                        </label>
                                    </div>

                                    <Grid
                                        container
                                        wrap="nowrap"
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="flex-start"
                                        style={{ width: '100%' }}
                                    >
                                        <Grid item style={{ height: 0 }}>
                                            <div className="form-item">
                                                <IconPicker
                                                    color={formData.color}
                                                    options={["color"]}
                                                    onChange={(color) => setFormData({ ...formData, color: color })}
                                                />
                                            </div>
                                        </Grid>
                                        <Grid zeroMinWidth item xs>
                                            <Input
                                                id="label"
                                                type="text"
                                                placeholder={messages['accountTagsMergeTags_label']}
                                                value={formData.label || ""}
                                                onChange={(value) => setFormData({ ...formData, label: value })}
                                                required
                                                requiredMessage={messages['required_field']}
                                                maxLength={50}
                                            />
                                        </Grid>
                                    </Grid>

                                    <div style={{ width: '100%', marginTop: 10 }}>
                                        <Checkbox
                                            id="nestUnder"
                                            label={messages['accountTagsMergeTags_nestUnder']}
                                            value={formData.nestUnder}
                                            onChange={(value) => {
                                                setFormData({ ...formData, nestUnder: value, parentId: null, tag: null });
                                                setErrorMessage({});
                                            }}
                                            size="small"
                                        />
                                    </div>

                                    {
                                        formData.nestUnder &&
                                        <div style={{ width: '100%', marginTop: 10 }}>
                                            <TagsManage
                                                id="tag"
                                                placeholder={messages["accountTagsMergeTags_typeToSearch"]}
                                                value={formData?.tag}
                                                mode="selector"
                                                onlyTags={true}
                                                multiple={false}
                                                height={100}
                                                menuStyle={{width: 430, height: 300}}
                                                isVisiblePill={true}
                                                disableInheritance={true}
                                                disableUntaggedSelection={true}
                                                onChange={(value) => {
                                                    if (formData?.tags?.length && formData?.tags?.find(t => t?.id == value.id)) {
                                                        setFormData({ ...formData, tag: null });
                                                        setErrorMessage({ ...errorMessage, nestUnder: messages['accountTagsMergeTags_errorParentTag'] });
                                                        return;
                                                    }
                                                    setErrorMessage({});
                                                    const tagSelected = value[0];
                                                    setFormData({ ...formData, tag: value, parentId: tagSelected?.id });
                                                }}
                                            />

                                            {
                                                errorMessage?.nestUnder && <small className='text-danger'>{errorMessage?.nestUnder}</small>
                                            }
                                        </div>
                                    }
                                </Form>
                            </TabContainer>
                            
                            {
                                
                                tabsWithPermissions.map((item, index) => {
                                    return (
                                        <TabContainer
                                            key={index}
                                            iconIndicator={item.iconIndicator}
                                            label={item.label}
                                            index={item.index}
                                            style={item.style}
                                            disabled={item.disabled}
                                        >
                                            {tab === item.index && renderTable()}
                                        </TabContainer>
                                    );
                                })
                                    
                            }
                        </Tabs>
                    </div>
                </ModalBody>

                <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
                    <Button
                        disabled={loading}
                        onClick={handleClose}
                        className="btn-cancel"
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loading}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            mergeTags.current.submit();
                        }}
                        className="btn-blue"
                    >
                        {messages['merge']}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                title={messages['accountTagsMergeTags_title']}
                open={openConfirmation}
                //position="right"
                size="small"
                style={{ overflow: 'hidden', height: 300, top: '30%' }}
                className="overflow-hidden"
                handleClose={() => setOpenConfirmation(false)}
            >
                <ModalBody
                    style={{
                        overflow: 'hidden',
                        marginBottom: 0
                    }}
                >
                    <div
                        style={{
                            padding: "0px 24px 24px 24px"
                        }}
                    >
                        <p>
                            <strong>
                                {messages['accountTagsMergeTags_confirmation'].replace("{number_tags}", formData?.tags?.length).replace("{tag_name}", formData?.label)}
                            </strong>
                        </p>
                    </div>
                </ModalBody>

                <ModalFooter style={{ boxShadow: "2px 2px 2px 1px rgb(2 0 0 / 94%)" }}>
                    <Button
                        onClick={() => setOpenConfirmation(false)}
                        className="btn-cancel"
                    >
                        {messages['cancel']}
                    </Button>

                    <Button
                        disabled={loading}
                        onClick={(e) => {
                            if (e) e.preventDefault();
                            dispatch(accountTagsMergeTagsSave({
                                ...formData,
                                color: formData?.color || '8677D9',
                                tagIds: formData?.tags?.map(tag => {
                                    return tag?.id
                                }),
                            }));
                            setOpenConfirmation(false);
                        }}
                        className="btn-blue"
                    >
                        {messages['merge']}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}

export default MergeTags;